import React, { useState } from 'react';
import { 
  BarChart2, Package, Users, FileText, Banknote, Settings, Smartphone,
  TrendingUp, Truck, ClipboardList, Store, HelpCircle, Search, ChevronDown,
  BookOpen, ShieldCheck, UserPlus, RotateCw, HandCoins, Target, Megaphone,
  WifiOff, CreditCard, Factory, X
} from 'lucide-react';

const WorkspaceGuide = () => {
  const [activeCategory, setActiveCategory] = useState('getting-started');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSection, setExpandedSection] = useState(null);

  const categories = [
    { id: 'getting-started', label: 'Premiers pas', icon: <BookOpen size={18} /> },
    { id: 'dashboard', label: 'Tableau de bord', icon: <BarChart2 size={18} /> },
    { id: 'products', label: 'Produits & Stock', icon: <Package size={18} /> },
    { id: 'clients', label: 'Clients', icon: <Users size={18} /> },
    { id: 'invoicing', label: 'Facturation', icon: <FileText size={18} /> },
    { id: 'finances', label: 'Finances', icon: <Banknote size={18} /> },
    { id: 'services', label: 'Services & Prestations', icon: <Target size={18} /> },
    { id: 'mobile-money', label: 'Mobile Money', icon: <Smartphone size={18} /> },
    { id: 'shop', label: 'Boutique en ligne', icon: <Store size={18} /> },
    { id: 'analytics', label: 'Analyse & Rapports', icon: <TrendingUp size={18} /> },
    { id: 'team', label: 'Équipe & Rôles', icon: <UserPlus size={18} /> },
    { id: 'settings', label: 'Paramètres', icon: <Settings size={18} /> },
    { id: 'faq', label: 'FAQ', icon: <HelpCircle size={18} /> },
  ];

  const allSections = {
    'getting-started': [
      {
        title: "Qu'est-ce que le Workspace ?",
        content: `Le Workspace est votre espace de gestion d'activité sur DJAMANN. Il centralise tous les outils dont vous avez besoin pour gérer votre business au quotidien.

🎯 À quoi sert le Workspace ?
• Gérer vos produits et votre stock
• Créer et suivre vos factures, devis et avoirs
• Enregistrer vos ventes et paiements
• Suivre vos clients et fournisseurs
• Analyser vos performances commerciales
• Gérer votre équipe

📊 Tout est centralisé
Depuis le menu latéral (sidebar), vous accédez à toutes les sections : produits, clients, factures, dépenses, stock, etc. Chaque section est conçue pour être simple et intuitive.`
      },
      {
        title: "Accéder au Workspace",
        content: `Pour accéder à votre Workspace :

1. Connectez-vous à votre compte DJAMANN
2. Cliquez sur "Mon Espace" ou accédez via le menu
3. Vous arrivez sur le tableau de bord principal

📱 Accès mobile
Le Workspace est optimisé pour mobile. Le menu latéral se transforme en menu déroulant accessible via le bouton ☰ en haut à gauche.

💡 Conseil : Ajoutez DJAMANN à l'écran d'accueil de votre téléphone pour un accès rapide comme une application native.`
      },
      {
        title: "Naviguer dans le Workspace",
        content: `Le Workspace est organisé en sections thématiques dans le menu latéral :

📦 Stock & Produits
Gérez vos produits, ravitaillements, fournisseurs et inventaires.

🛒 Ventes & Clients
Boutique en ligne, gestion des clients et marketing.

🧾 Facturation
Factures, devis, avoirs et retours.

💰 Finances
Paiements, dépenses, dépôts & prêts.

🔧 Services
Prestations et demandes de services.

📱 Mobile Money
Point de transfert Mobile Money.

📈 Analyse & Rapports
Bilan financier et analyse des ventes.

👥 Organisation
Équipe, paramètres et mode hors ligne.

💡 Cliquez sur une section pour y accéder. La section active est mise en surbrillance dans le menu.`
      },
      {
        title: "Les modules et abonnements",
        content: `Votre Workspace fonctionne avec un système modulaire :

📦 Qu'est-ce qu'un module ?
Chaque grande fonctionnalité (Boutique, Mobile Money, etc.) peut nécessiter un module actif dans votre abonnement.

🔓 Modules inclus par défaut
La plupart des fonctionnalités de base (produits, clients, factures, paiements, stock) sont disponibles avec votre abonnement standard.

🔑 Modules additionnels
Certaines fonctionnalités avancées (boutique en ligne, marketing, etc.) peuvent nécessiter l'activation d'un module spécifique.

📋 Vérifier vos modules
Allez dans Paramètres → Mon abonnement pour voir vos modules actifs et en activer de nouveaux.`
      }
    ],
    'dashboard': [
      {
        title: "Comprendre le tableau de bord",
        content: `Le tableau de bord est la première page de votre Workspace. Il vous donne une vue d'ensemble de votre activité.

📊 Indicateurs principaux
• Chiffre d'affaires du jour/semaine/mois
• Nombre de ventes
• Nombre de clients
• Produits en stock faible
• Dernières transactions

📈 Graphiques
• Évolution du chiffre d'affaires dans le temps
• Répartition des ventes par catégorie
• Tendances et comparaisons

🔔 Alertes
• Produits en rupture de stock
• Factures impayées
• Paiements en attente

💡 Le tableau de bord se met à jour en temps réel à chaque nouvelle transaction.`
      },
      {
        title: "Personnaliser l'affichage",
        content: `Vous pouvez adapter l'affichage du tableau de bord :

📅 Périodes
Changez la période d'affichage :
• Aujourd'hui
• Cette semaine
• Ce mois
• Personnalisée

📊 Widgets
Les widgets affichent les informations les plus pertinentes pour votre activité. Consultez-les régulièrement pour garder le contrôle.

💡 Conseil : Consultez votre tableau de bord chaque matin pour avoir une vue claire de votre journée.`
      }
    ],
    'products': [
      {
        title: "Ajouter un produit",
        content: `Pour ajouter un nouveau produit à votre catalogue :

📝 Étapes
1. Allez dans « Produits & Stock »
2. Cliquez sur « + Nouveau produit »
3. Remplissez les informations :
   • Nom du produit
   • Description (optionnelle)
   • Prix de vente
   • Prix d'achat (pour le calcul de marge)
   • Catégorie
   • Quantité en stock initiale
   • Photo du produit (optionnelle)
4. Enregistrez

💰 Prix et marges
• Le prix d'achat sert à calculer automatiquement votre marge bénéficiaire
• Vous pouvez modifier les prix à tout moment

📸 Photos
• Ajoutez une photo pour identifier rapidement vos produits
• Utile aussi pour la boutique en ligne

💡 Conseil : Organisez vos produits par catégories pour les retrouver facilement.`
      },
      {
        title: "Gérer le stock",
        content: `La gestion du stock vous aide à ne jamais être en rupture :

📦 Stock actuel
La page « Produits & Stock » affiche le stock actuel de chaque produit. Les produits en stock faible sont signalés en rouge/orange.

🔄 Mouvements de stock
Le stock change automatiquement quand :
• Vous créez une facture (stock diminue)
• Vous créez un avoir/retour (stock augmente)
• Vous effectuez un ravitaillement (stock augmente)
• Vous faites un inventaire (ajustement)

⚠️ Alertes de stock
Configurez un seuil d'alerte pour chaque produit. Quand le stock passe en dessous, vous êtes averti.

📋 Historique des mouvements
Consultez « Analyse Ventes → Mouvements » pour voir l'historique complet des entrées/sorties de stock.`
      },
      {
        title: "Ravitaillement (Réappro)",
        content: `Le ravitaillement permet d'enregistrer vos achats de marchandises :

🚚 Créer un ravitaillement
1. Allez dans « Ravitaillement »
2. Cliquez sur « + Nouveau ravitaillement »
3. Sélectionnez le fournisseur (ou créez-en un)
4. Ajoutez les produits ravitaillés avec les quantités
5. Indiquez le coût total
6. Validez

📊 Impact automatique
• Le stock des produits est mis à jour automatiquement
• Le coût est enregistré dans vos dépenses
• L'historique est conservé

📜 Historique
Consultez « Ravitaillement → Historique » pour voir tous vos ravitaillements passés.`
      },
      {
        title: "Fournisseurs",
        content: `Gérez vos fournisseurs pour un suivi efficace :

👤 Ajouter un fournisseur
1. Allez dans « Fournisseurs »
2. Cliquez sur « + Nouveau fournisseur »
3. Renseignez : nom, contact, adresse, notes

🔗 Lien avec les ravitaillements
Quand vous créez un ravitaillement, vous pouvez sélectionner le fournisseur. Cela permet de :
• Suivre les achats par fournisseur
• Comparer les fournisseurs
• Garder un historique`
      },
      {
        title: "Inventaires",
        content: `L'inventaire permet de vérifier et corriger votre stock réel :

📋 Créer un inventaire
1. Allez dans « Inventaires »
2. Cliquez sur « + Nouvel inventaire »
3. Pour chaque produit, saisissez la quantité réelle comptée
4. Le système calcule les écarts automatiquement
5. Validez pour ajuster le stock

⚠️ Écarts
• Écart positif : vous avez plus de stock que prévu
• Écart négatif : vous avez moins de stock que prévu (perte, vol, casse...)
• L'historique des écarts est conservé pour analyse

💡 Conseil : Faites un inventaire régulier (au moins mensuel) pour garder un stock fiable.`
      }
    ],
    'clients': [
      {
        title: "Ajouter un client",
        content: `Enregistrez vos clients pour un meilleur suivi :

👤 Créer un client
1. Allez dans « Clients »
2. Cliquez sur « + Nouveau client »
3. Renseignez les informations :
   • Nom complet
   • Téléphone
   • Email (optionnel)
   • Adresse (optionnelle)
   • Notes (optionnelles)
4. Enregistrez

💡 Pourquoi enregistrer les clients ?
• Créer des factures à leur nom
• Suivre l'historique d'achats par client
• Gérer les créances et dettes
• Envoyer des factures par email/WhatsApp
• Fidéliser avec des offres ciblées`
      },
      {
        title: "Suivi et historique client",
        content: `Chaque fiche client centralise toutes les informations :

📊 Informations visibles
• Coordonnées du client
• Historique de toutes les factures
• Montant total des achats
• Paiements effectués
• Solde restant (créances)

🔍 Recherche
Utilisez la barre de recherche pour trouver rapidement un client par nom ou téléphone.

📱 Contact rapide
Cliquez sur le numéro de téléphone pour appeler directement ou envoyer un message.`
      }
    ],
    'invoicing': [
      {
        title: "Créer une facture",
        content: `La facture est le document principal de vos ventes :

🧾 Étapes de création
1. Allez dans « Factures »
2. Cliquez sur « + Nouvelle facture »
3. Sélectionnez ou créez un client
4. Ajoutez les produits/services vendus :
   • Recherchez le produit
   • Indiquez la quantité
   • Le prix et le total se calculent automatiquement
5. Vérifiez le résumé (sous-total, remise, total)
6. Validez la facture

📊 Impact automatique
• Le stock est diminué pour chaque produit facturé
• La vente est enregistrée dans vos statistiques
• Un numéro de facture unique est attribué

💰 Statut de paiement
• Payée : le client a tout réglé
• Partiellement payée : il reste un solde
• Impayée : aucun paiement enregistré

📤 Partage
Vous pouvez partager la facture :
• Par WhatsApp
• Par email
• Télécharger en PDF
• Imprimer directement`
      },
      {
        title: "Créer un devis",
        content: `Le devis permet de proposer une offre avant la vente :

📝 Créer un devis
1. Allez dans « Devis »
2. Cliquez sur « + Nouveau devis »
3. Ajoutez les produits/services avec quantités et prix
4. Le total se calcule automatiquement
5. Envoyez au client

🔄 Convertir en facture
Si le client accepte le devis :
• Ouvrez le devis
• Cliquez sur « Convertir en facture »
• La facture est créée automatiquement avec les mêmes éléments

⏳ Validité
Un devis a une durée de validité. Passé ce délai, le client devra demander un nouveau devis.

💡 Conseil : Utilisez les devis pour les gros montants. Cela donne une image professionnelle et protège les deux parties.`
      },
      {
        title: "Avoirs et retours",
        content: `Les avoirs permettent de gérer les retours produits et remboursements :

↩️ Qu'est-ce qu'un avoir ?
Un avoir est un document qui annule partiellement ou totalement une facture. Il est utilisé quand :
• Un client retourne un produit
• Un produit est défectueux
• Une erreur de facturation

📝 Créer un avoir
1. Allez dans « Avoirs / Retours »
2. Cliquez sur « + Nouvel avoir »
3. Sélectionnez la facture d'origine
4. Choisissez les produits retournés et quantités
5. Validez

📊 Impact automatique
• Le stock est augmenté pour les produits retournés
• Le montant est déduit du solde client
• L'avoir est lié à la facture d'origine

💡 L'avoir est un document officiel qui justifie le retour. Conservez-le pour votre comptabilité.`
      },
      {
        title: "Bons de livraison",
        content: `Les bons de livraison accompagnent vos expéditions :

📦 Qu'est-ce qu'un bon de livraison ?
C'est un document qui liste les produits livrés au client. Il sert de preuve de livraison.

📝 Créer un bon de livraison
Les bons de livraison sont générés à partir d'une facture :
1. Ouvrez une facture
2. Cliquez sur « Générer un bon de livraison »
3. Indiquez les quantités livrées (peut être une livraison partielle)
4. Le bon est créé et imprimable

📋 Suivi
• Bon en attente : marchandise pas encore livrée
• Bon livré : marchandise remise au client
• Livraison partielle : certains produits restent à livrer

✅ Validation
Le client peut signer (physiquement ou numériquement) le bon de livraison pour confirmer la réception.

💡 Les bons de livraison sont essentiels pour les commandes à distance ou les livraisons différées.`
      }
    ],
    'finances': [
      {
        title: "Enregistrer un paiement",
        content: `Suivez tous les paiements reçus :

💳 Enregistrer un paiement
1. Allez dans « Paiements »
2. Cliquez sur « + Nouveau paiement »
3. Sélectionnez le client et la facture
4. Indiquez le montant reçu
5. Choisissez le mode de paiement (espèces, mobile money, virement, etc.)
6. Validez

📊 Paiements partiels
Un client peut payer en plusieurs fois :
• Chaque paiement partiel est enregistré
• Le solde restant est mis à jour automatiquement
• La facture passe en « partiellement payée »

🧾 Reçu
Un reçu de paiement est généré automatiquement et peut être partagé au client.`
      },
      {
        title: "Gérer les dépenses",
        content: `Enregistrez toutes vos dépenses pour un suivi précis :

💸 Types de dépenses
• Achats de marchandises (ravitaillements)
• Loyer et charges
• Transport
• Salaires
• Marketing/publicité
• Fournitures
• Autres

📝 Ajouter une dépense
1. Allez dans « Dépenses »
2. Cliquez sur « + Nouvelle dépense »
3. Renseignez :
   • Montant
   • Catégorie
   • Description
   • Date
4. Validez

📊 Analyse
Vos dépenses apparaissent dans :
• Le bilan financier
• Les rapports mensuels
• Le calcul du bénéfice net

💡 Conseil : Enregistrez chaque dépense le jour même pour ne rien oublier.`
      },
      {
        title: "Dépôts et prêts",
        content: `La section « Dépôts & Prêts » gère les transactions de confiance :

🤝 Dépôts
Quand un client vous confie un article ou de l'argent en dépôt :
• Enregistrez le dépôt avec les détails
• Suivez la durée
• Notifiez le client pour la récupération

💰 Prêts
Quand vous prêtez de l'argent ou des articles :
• Enregistrez le montant ou l'article
• Fixez une date de retour
• Suivez les remboursements

📊 Suivi
• Dépôts/prêts en cours
• Dépôts/prêts remboursés
• Historique complet

⚠️ Les dépôts et prêts n'impactent pas directement votre comptabilité mais sont essentiels pour le suivi de confiance avec vos partenaires.`
      },
      {
        title: "Dettes",
        content: `Suivez les dettes (ce que vous devez ou ce qu'on vous doit) :

📋 Types de dettes
• Dettes fournisseurs : ce que vous devez à vos fournisseurs
• Créances clients : ce que vos clients vous doivent

📝 Enregistrer une dette
1. Accédez via « Finances »
2. Ajoutez une dette avec :
   • Montant
   • Débiteur/créancier
   • Échéance
   • Notes

🔔 Suivi
• Alerte quand une dette arrive à échéance
• Historique des remboursements
• Solde restant mis à jour

💡 Un bon suivi des dettes est essentiel pour la santé financière de votre activité.`
      }
    ],
    'services': [
      {
        title: "Prestations de services",
        content: `Si vous proposez des services en plus des produits :

🔧 Ajouter une prestation
1. Allez dans « Prestations »
2. Cliquez sur « + Nouvelle prestation »
3. Décrivez le service :
   • Nom du service
   • Description
   • Tarif
   • Durée estimée
4. Enregistrez

🧾 Facturer une prestation
Les prestations peuvent être ajoutées sur une facture au même titre qu'un produit.

📊 Suivi
• Liste de toutes vos prestations
• Historique des prestations effectuées
• Chiffre d'affaires par prestation`
      },
      {
        title: "Demandes de services reçues",
        content: `Si vous avez une boutique de services, vous recevez des demandes :

📩 Recevoir une demande
Quand un client commande via votre boutique, vous recevez une notification avec :
• Détails du service demandé
• Coordonnées du client
• Message du client

✅ Traiter la demande
1. Consultez la demande dans « Revenus Services »
2. Acceptez ou refusez
3. Si acceptée, réalisez le service
4. Marquez comme terminé
5. Le paiement est enregistré

💡 Répondez rapidement aux demandes pour maintenir une bonne réputation.`
      }
    ],
    'mobile-money': [
      {
        title: "Point de transfert Mobile Money",
        content: `Si vous êtes un point de transfert Mobile Money, cette section est faite pour vous :

📱 Qu'est-ce que c'est ?
Gérez vos opérations de transfert d'argent mobile (Orange Money, MTN Money, Wave, Moov Money, etc.)

💰 Fonctionnalités
• Enregistrer les dépôts (argent reçu)
• Enregistrer les retraits (argent distribué)
• Enregistrer les transferts
• Suivre votre solde de caisse
• Calculer vos commissions

📊 Tableau de bord Mobile Money
• Solde actuel par opérateur
• Transactions du jour
• Commissions gagnées
• Historique complet

🧮 Calcul des commissions
Les commissions sont calculées automatiquement selon les barèmes de chaque opérateur.

💡 Enregistrez chaque opération immédiatement pour ne pas perdre le fil de votre caisse.`
      }
    ],
    'shop': [
      {
        title: "Configurer sa boutique en ligne",
        content: `La boutique en ligne permet de vendre vos produits et services sur Internet :

🛍️ Activer la boutique
1. Allez dans « Boutique en ligne »
2. Configurez votre boutique :
   • Nom de la boutique
   • Description
   • Logo
   • Lien personnalisé (slug)
3. Activez la boutique

🌐 Votre lien unique
Votre boutique est accessible via un lien du type :
djamann.com/boutique/votre-nom

📦 Ajouter des articles
Les produits de votre catalogue peuvent être publiés sur la boutique :
• Activez « Visible en boutique » pour chaque produit souhaité
• Ajoutez des photos attrayantes
• Rédigez des descriptions vendeuses

💡 Partagez le lien de votre boutique sur vos réseaux sociaux et WhatsApp pour attirer des clients.`
      },
      {
        title: "Gérer les commandes",
        content: `Quand un client commande sur votre boutique :

📩 Notification
Vous recevez une notification avec :
• Détails de la commande
• Coordonnées du client
• Mode de livraison choisi

📋 Traiter une commande
1. Allez dans « Boutique → Commandes »
2. Consultez la commande
3. Confirmez la disponibilité
4. Préparez la commande
5. Marquez comme expédiée/livrée

📊 Statuts de commande
• Nouvelle : commande reçue
• Confirmée : vous avez accepté
• En préparation : commande en cours de préparation
• Expédiée : envoyée au client
• Livrée : reçue par le client
• Annulée : commande annulée

💡 Conseil : Traitez les commandes rapidement pour satisfaire vos clients et obtenir de bonnes évaluations.`
      }
    ],
    'analytics': [
      {
        title: "Bilan financier",
        content: `Le bilan vous donne une vue complète de vos finances :

📊 Éléments du bilan
• Chiffre d'affaires total
• Dépenses totales
• Bénéfice net (CA - Dépenses)
• Marge bénéficiaire en %

📅 Périodes
Consultez votre bilan par :
• Jour
• Semaine
• Mois
• Trimestre
• Année
• Période personnalisée

📈 Graphiques
• Évolution du CA
• Répartition des dépenses par catégorie
• Comparaison mois par mois

💡 Consultez votre bilan au moins une fois par semaine pour piloter votre activité efficacement.`
      },
      {
        title: "Analyse des ventes",
        content: `L'analyse des ventes vous aide à comprendre ce qui fonctionne :

📊 Indicateurs clés
• Produits les plus vendus
• Produits les moins vendus
• Clients les plus fidèles
• Heures/jours de vente les plus actifs
• Panier moyen

📈 Tendances
• Évolution des ventes dans le temps
• Comparaison entre périodes
• Saisonnalité

🏆 Top produits
Identifiez vos meilleurs produits pour :
• Ajuster vos stocks (plus de stock pour les best-sellers)
• Adapter vos prix
• Orienter vos achats

📉 Produits à surveiller
Les produits qui se vendent peu pourraient nécessiter :
• Une baisse de prix
• Une mise en avant
• Un retrait du catalogue

💡 Utilisez ces données pour prendre des décisions éclairées sur vos achats et votre stratégie commerciale.`
      },
      {
        title: "Historique des mouvements de stock",
        content: `Suivez chaque entrée et sortie de stock :

📋 Types de mouvements
• Entrée (ravitaillement) : stock +
• Sortie (vente/facture) : stock -
• Retour (avoir) : stock +
• Ajustement (inventaire) : stock ± selon l'écart

🔍 Filtres disponibles
• Par produit
• Par type de mouvement
• Par période
• Par source (facture, ravitaillement, inventaire)

📊 Utilité
• Identifier les pertes ou vols
• Comprendre les tendances de consommation
• Justifier les écarts d'inventaire
• Optimiser vos commandes fournisseur`
      }
    ],
    'team': [
      {
        title: "Inviter un membre dans l'équipe",
        content: `Vous pouvez inviter d'autres utilisateurs DJAMANN à gérer votre workspace :

👥 Comment inviter ?
1. Allez dans « Mon Équipe »
2. Cliquez sur « + Inviter un membre »
3. Entrez l'email du membre (il doit avoir un compte DJAMANN)
4. Choisissez un rôle prédéfini
5. Ajustez les permissions si nécessaire
6. Envoyez l'invitation

📧 Processus d'invitation
• Le membre reçoit une notification
• Il peut accepter ou refuser
• Une fois accepté, il accède à votre workspace selon ses permissions

💡 Vous pouvez inviter autant de membres que nécessaire. Chaque membre a son propre accès avec des permissions spécifiques.`
      },
      {
        title: "Les rôles prédéfinis",
        content: `Chaque rôle a des permissions adaptées à la fonction :

🧾 Caissier(ère)
Pour la personne à la caisse. Peut :
• Voir les produits et leurs prix
• Créer et consulter des factures
• Enregistrer les paiements
• Voir et créer des clients
• Consulter les devis
• Voir les retours/avoirs
• Gérer le Mobile Money

🛒 Vendeur(se)
Pour la personne qui vend. Peut :
• Voir et modifier les produits
• Gérer les clients (voir, créer, modifier)
• Enregistrer les paiements
• Créer des factures et des devis
• Voir les prestations
• Créer et gérer les bons de livraison
• Créer des avoirs/retours

👔 Superviseur(e)
Pour superviser l'activité. Peut :
• Voir le tableau de bord
• Gérer les produits et le stock
• Gérer les clients
• Enregistrer les paiements
• Gérer les factures, devis et prestations
• Voir les dépenses et les dettes
• Gérer les bons de livraison et retours
• Voir les analyses de ventes et rapports
• Voir l'équipe et le Mobile Money

📊 Comptable
Pour la gestion financière. Peut :
• Voir le tableau de bord
• Voir les produits et clients
• Voir les paiements
• Gérer les dépenses complètement
• Gérer les dettes
• Créer et modifier les factures et devis
• Voir les retours et analyses de ventes
• Gérer les dépôts & prêts
• Voir le Mobile Money et les rapports

💼 Manager
Accès quasi-complet. Peut faire tout sauf :
• Supprimer le workspace
• Gérer l'abonnement du propriétaire
C'est le rôle idéal pour un bras droit de confiance.

🚚 Livreur(se)
Pour les livraisons. Peut :
• Voir les produits et clients
• Voir les factures
• Voir les ravitaillements
• Gérer les bons de livraison
• Voir les retours

📦 Magasinier(ère)
Pour la gestion du stock. Peut :
• Voir, modifier et créer des produits
• Effectuer des ravitaillements
• Gérer les inventaires
• Voir les mouvements de stock
• Gérer les bons de livraison
• Créer des avoirs/retours

⚙️ Rôle personnalisé
Choisissez exactement les permissions que vous voulez attribuer, sans rôle prédéfini.`
      },
      {
        title: "Gérer les permissions",
        content: `Chaque membre a des permissions spécifiques :

🔐 Comment ça marche ?
Quand vous sélectionnez un rôle, les permissions correspondantes sont automatiquement cochées. Vous pouvez ensuite :
• Ajouter des permissions supplémentaires
• Retirer des permissions qui ne conviennent pas
• Le tout en cochant/décochant les cases

📋 Catégories de permissions
• Tableau de bord : voir les statistiques
• Produits : voir, créer, modifier, supprimer
• Clients : voir, créer, modifier
• Paiements : voir, enregistrer
• Dépenses : voir, créer, modifier
• Dettes : voir, créer
• Factures : voir, créer, modifier
• Devis : voir, créer, modifier
• Prestations : voir, créer
• Stock : ravitaillement, inventaires, mouvements
• Livraisons : voir et gérer les bons
• Retours : voir et créer des avoirs
• Analyses : ventes, mouvements de stock
• Dépôts & Prêts : voir et gérer
• Mobile Money : voir et gérer
• Rapports : voir le bilan
• Paramètres, Équipe, Boutique : voir et gérer

⚠️ Principe de moindre privilège
N'accordez que les permissions nécessaires à la fonction du membre. Par exemple, un caissier n'a pas besoin de voir les dépenses ou de modifier les paramètres.

💡 Vous pouvez modifier les permissions d'un membre à tout moment.`
      },
      {
        title: "Workspaces partagés (invitations reçues)",
        content: `Si quelqu'un vous invite dans son workspace :

📩 Recevoir une invitation
• Vous recevez une notification
• L'invitation apparaît dans « Workspaces partagés »

✅ Accepter
1. Allez dans « Workspaces partagés »
2. Consultez l'invitation (rôle, permissions, workspace)
3. Cliquez sur « Accepter »
4. Vous avez maintenant accès au workspace avec vos permissions

❌ Refuser
Cliquez sur « Refuser » si vous ne souhaitez pas rejoindre.

🔄 Basculer entre workspaces
Vous pouvez facilement passer de votre propre workspace aux workspaces auxquels vous êtes invité.

🚪 Quitter un workspace
Vous pouvez quitter un workspace partagé à tout moment.`
      }
    ],
    'settings': [
      {
        title: "Paramètres du workspace",
        content: `Les paramètres vous permettent de personnaliser votre espace :

⚙️ Informations du business
• Nom de l'entreprise/activité
• Logo
• Adresse
• Numéro de téléphone
• Email professionnel

🧾 Paramètres de facturation
• Numérotation des factures
• Mentions légales
• Devise
• Taux de TVA (si applicable)

🎨 Personnalisation
• Thème de couleur
• Informations affichées sur les factures
• Logo sur les documents

💡 Conseil : Remplissez tous les paramètres dès le départ pour que vos documents (factures, devis) soient professionnels.`
      },
      {
        title: "Mode hors ligne",
        content: `Le mode hors ligne vous permet de travailler sans Internet :

📴 Comment ça fonctionne ?
1. Allez dans « Mode Hors Ligne »
2. Activez le mode hors ligne
3. Vos données sont sauvegardées localement
4. Vous pouvez continuer à créer des factures et enregistrer des ventes

🔄 Synchronisation
Quand vous retrouvez une connexion Internet :
• Les données créées hors ligne sont synchronisées automatiquement
• Aucune perte de données

⚠️ Limitations
Certaines fonctionnalités nécessitent Internet :
• Envoi de factures par email
• Mise à jour du stock en temps réel
• Notifications aux membres de l'équipe

💡 Idéal pour les marchés, les zones rurales ou les périodes de coupure réseau.`
      }
    ],
    'faq': [
      {
        title: "Questions sur les produits et le stock",
        content: `❓ Comment modifier le prix d'un produit ?
Allez dans « Produits », cliquez sur le produit, modifiez le prix et enregistrez. Les factures déjà créées ne sont pas impactées.

❓ Mon stock est négatif, que faire ?
Un stock négatif signifie que vous avez vendu plus que ce que vous aviez en stock. Faites un ravitaillement pour corriger la situation, ou effectuez un inventaire pour ajuster.

❓ Comment supprimer un produit ?
Allez dans les détails du produit et cliquez sur « Supprimer ». Attention : les factures existantes liées à ce produit ne seront pas affectées, mais vous ne pourrez plus le sélectionner pour de nouvelles factures.

❓ Puis-je importer mes produits en masse ?
Actuellement, les produits doivent être ajoutés un par un. Une fonctionnalité d'import est prévue.

❓ Comment créer des catégories de produits ?
Les catégories sont créées automatiquement quand vous assignez une catégorie à un produit. Vous pouvez choisir parmi les catégories existantes ou en créer une nouvelle.`
      },
      {
        title: "Questions sur les factures",
        content: `❓ Comment annuler une facture ?
Vous ne pouvez pas supprimer une facture validée (obligation comptable). Créez plutôt un avoir/retour qui annule la facture.

❓ Comment modifier une facture ?
Si la facture n'est pas encore finalisée, vous pouvez la modifier. Sinon, créez un avoir pour corriger.

❓ La facture est-elle conforme légalement ?
Nos factures contiennent tous les éléments légaux requis : numéro, date, détails du vendeur et de l'acheteur, description des biens/services, montants.

❓ Comment envoyer une facture au client ?
Ouvrez la facture et utilisez les boutons de partage : WhatsApp, email, télécharger en PDF, ou imprimer.

❓ Comment voir les factures impayées ?
Dans la liste des factures, filtrez par statut « Impayée » ou « Partiellement payée ».

❓ Le stock se met-il à jour automatiquement ?
Oui ! Quand vous créez une facture, le stock des produits concernés est automatiquement diminué.`
      },
      {
        title: "Questions sur les paiements",
        content: `❓ Un client veut payer en plusieurs fois, comment faire ?
Créez la facture normalement. Puis enregistrez chaque paiement partiel séparément. Le solde restant se met à jour automatiquement.

❓ Comment modifier un paiement enregistré ?
Les paiements ne peuvent pas être modifiés après enregistrement (traçabilité). En cas d'erreur, enregistrez un nouveau paiement correctif.

❓ Quels modes de paiement puis-je enregistrer ?
Espèces, Mobile Money (tous opérateurs), virement bancaire, chèque, carte bancaire, et autres.

❓ Comment savoir combien un client me doit ?
Ouvrez la fiche du client pour voir son solde, ou filtrez les factures par statut impayé.`
      },
      {
        title: "Questions sur l'équipe",
        content: `❓ Combien de membres puis-je inviter ?
Il n'y a pas de limite. Invitez autant de membres que nécessaire.

❓ Un membre peut-il voir mes dépenses ?
Seulement si vous lui avez accordé la permission « Voir les dépenses ». Chaque permission est individuelle.

❓ Comment retirer un membre de l'équipe ?
Dans « Mon Équipe », cliquez sur le membre et choisissez « Retirer ». Son accès est immédiatement révoqué.

❓ Un membre peut-il modifier mes paramètres ?
Seulement si vous lui avez accordé la permission « Voir les paramètres ». Seul le Manager a cette permission par défaut.

❓ Que voit un membre quand il accède à mon workspace ?
Exactement ce que ses permissions lui autorisent. Les sections non autorisées ne sont pas visibles dans son menu.

❓ Le membre utilise-t-il son propre compte ?
Oui ! Chaque membre se connecte avec son propre compte DJAMANN. Il voit votre workspace dans « Workspaces partagés ».

❓ Puis-je changer le rôle d'un membre ?
Oui, à tout moment. Allez dans « Mon Équipe », cliquez sur le membre, changez son rôle et ses permissions.`
      },
      {
        title: "Questions sur la boutique en ligne",
        content: `❓ Ma boutique est-elle visible par tous ?
Oui, une fois activée, votre boutique est publique et accessible via son lien unique.

❓ Comment les clients me contactent-ils ?
Via le formulaire de commande de la boutique ou les coordonnées affichées.

❓ Puis-je cacher certains produits de la boutique ?
Oui, chaque produit peut être mis « visible » ou « masqué » dans la boutique indépendamment.

❓ Comment les clients paient-ils ?
Selon la configuration de votre boutique : Mobile Money, espèces à la livraison, ou autre.

❓ Comment partager ma boutique ?
Copiez le lien de votre boutique et partagez-le sur WhatsApp, Facebook, Instagram, ou tout autre réseau.`
      },
      {
        title: "Questions sur le Mobile Money",
        content: `❓ Dois-je être un point agréé ?
Cette section est conçue pour les agents/points de transfert. Si vous n'êtes pas un point de transfert, vous n'avez pas besoin de cette section.

❓ Comment équilibrer ma caisse ?
Enregistrez chaque opération (dépôt, retrait, transfert) immédiatement. Le système calcule votre solde en temps réel.

❓ Les commissions sont-elles calculées automatiquement ?
Oui, selon les barèmes des opérateurs configurés.

❓ Puis-je gérer plusieurs opérateurs ?
Oui ! Gérez Orange Money, MTN Money, Wave, Moov Money et d'autres depuis la même interface.`
      },
      {
        title: "Problèmes courants et solutions",
        content: `🔧 Je ne vois pas une section dans le menu
• Vérifiez que le module correspondant est actif dans votre abonnement
• Si vous êtes un membre invité, vérifiez vos permissions

🔧 Mon stock ne correspond pas
• Effectuez un inventaire pour identifier les écarts
• Vérifiez l'historique des mouvements de stock
• Assurez-vous que toutes les ventes ont été facturées

🔧 Je ne peux pas créer de facture
• Vérifiez que vous avez au moins un produit/service
• Vérifiez votre abonnement
• Si membre invité : vérifiez que vous avez la permission « Créer des factures »

🔧 Un membre ne reçoit pas son invitation
• Vérifiez que l'email est correct
• Le membre doit avoir un compte DJAMANN avec cet email
• Demandez-lui de vérifier ses notifications

🔧 Ma facture n'apparaît pas dans les ventes
• Assurez-vous que la facture est validée (pas en brouillon)
• Rafraîchissez la page
• Vérifiez la période sélectionnée dans les filtres

🔧 Le mode hors ligne ne fonctionne pas
• Activez-le quand vous avez encore une connexion
• Assurez-vous que votre navigateur supporte le stockage local
• Videz le cache si nécessaire

🔧 Autre problème ?
Contactez le support : support@djamann.com`
      }
    ]
  };

  // Get current sections based on active category
  const currentSections = allSections[activeCategory] || [];

  // Filter sections by search query
  const filteredSections = searchQuery
    ? Object.entries(allSections).flatMap(([category, sections]) =>
        sections
          .filter(s => 
            s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            s.content.toLowerCase().includes(searchQuery.toLowerCase())
          )
          .map(s => ({ ...s, category }))
      )
    : currentSections;

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-2xl mb-8 overflow-hidden">
        <div className="px-6 py-8 sm:px-8 sm:py-10">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
              <BookOpen size={28} />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">Guide d'utilisation</h1>
              <p className="text-white/80 text-sm sm:text-base">Tout savoir pour bien gérer votre workspace</p>
            </div>
          </div>
          
          {/* Search bar */}
          <div className="mt-6 relative max-w-xl">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher dans le guide..."
              className="w-full pl-12 pr-12 py-3.5 rounded-xl bg-white/20 text-white placeholder-white/60 border border-white/30 focus:outline-none focus:ring-2 focus:ring-white/50 focus:bg-white/30 transition-all text-sm sm:text-base"
            />
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/70" />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white"
              >
                <X size={18} />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar - Categories */}
        {!searchQuery && (
          <div className="lg:w-56 flex-shrink-0">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-3 sticky top-6">
              <h2 className="font-bold text-gray-800 mb-3 px-3 text-sm">Catégories</h2>
              <nav className="space-y-0.5">
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => {
                      setActiveCategory(cat.id);
                      setExpandedSection(null);
                    }}
                    className={`w-full text-left px-3 py-2.5 rounded-xl flex items-center gap-2.5 transition-all text-sm ${
                      activeCategory === cat.id
                        ? 'bg-emerald-100 text-emerald-700 font-medium'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <span className="flex-shrink-0">{cat.icon}</span>
                    <span className="truncate">{cat.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Search results header */}
          {searchQuery && (
            <div className="mb-6 p-4 bg-emerald-50 rounded-xl border border-emerald-100">
              <p className="text-emerald-700 text-sm">
                <span className="font-medium">{filteredSections.length}</span> résultat(s) pour « {searchQuery} »
              </p>
            </div>
          )}

          {/* Category title */}
          {!searchQuery && (
            <div className="mb-6">
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-3">
                <span>{categories.find(c => c.id === activeCategory)?.icon}</span>
                {categories.find(c => c.id === activeCategory)?.label}
              </h2>
            </div>
          )}

          {/* Sections */}
          <div className="space-y-3">
            {filteredSections.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
                <span className="text-5xl mb-4 block">🔍</span>
                <h3 className="text-lg font-bold text-gray-700 mb-2">Aucun résultat</h3>
                <p className="text-gray-500 text-sm">Essayez avec d'autres mots-clés</p>
              </div>
            ) : (
              filteredSections.map((section, index) => (
                <div 
                  key={index}
                  className={`bg-white rounded-2xl shadow-sm border transition-all duration-300 ${
                    expandedSection === index ? 'border-emerald-300 shadow-md' : 'border-gray-100 hover:shadow-md'
                  }`}
                >
                  <button
                    onClick={() => setExpandedSection(expandedSection === index ? null : index)}
                    className="w-full p-5 text-left flex items-center justify-between gap-4"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {searchQuery && section.category && (
                        <span className="px-2 py-1 bg-gray-100 rounded-lg text-xs text-gray-500 flex-shrink-0">
                          {categories.find(c => c.id === section.category)?.label}
                        </span>
                      )}
                      <h3 className="text-base font-semibold text-gray-800 truncate">{section.title}</h3>
                    </div>
                    <ChevronDown 
                      size={18}
                      className={`text-gray-400 transition-transform flex-shrink-0 ${
                        expandedSection === index ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${
                    expandedSection === index ? 'max-h-[3000px]' : 'max-h-0'
                  }`}>
                    <div className="px-5 pb-5 text-gray-600 leading-relaxed whitespace-pre-line border-t border-gray-100 pt-4 text-sm">
                      {section.content}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Footer help */}
      <div className="mt-10 mb-6 text-center">
        <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
          <p className="text-gray-500 text-sm mb-1">Vous ne trouvez pas la réponse ?</p>
          <p className="text-gray-700 font-medium text-sm">
            Contactez-nous à <span className="text-emerald-600">support@djamann.com</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default WorkspaceGuide;
